import { type ComponentType, type SVGProps } from 'react';

import { cn } from '@/utils/classname';

export interface ViewToggleOption<T extends string> {
  value: T;
  label: string;
  /** Heroicon (or any SVG component) rendered before the label. */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

export interface ViewToggleProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  /** 2–3 mutually-exclusive views (Hick's Law — keep it to three). */
  options: ViewToggleOption<T>[];
  'aria-label': string;
  className?: string;
}

/**
 * Segmented control for switching between 2–3 mutually-exclusive views of the
 * same data (grid/list, graph/narrative). Renders a `role="tablist"` with an
 * `aria-selected` tab per option and the brand keyboard focus ring.
 *
 * Generic over the view union so callers stay type-safe:
 * `<ViewToggle<'grid' | 'table'> … />`.
 */
export function ViewToggle<T extends string>({
  value,
  onChange,
  options,
  'aria-label': ariaLabel,
  className,
}: ViewToggleProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'bg-chariot-background-secondary border-chariot-stroke inline-flex rounded-lg border p-0.5',
        className
      )}
    >
      {options.map(({ value: optionValue, label, icon: Icon }) => {
        const isActive = optionValue === value;
        return (
          <button
            key={optionValue}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(optionValue)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
              'focus-visible:ring-chariot-brand-primary focus:outline-hidden focus-visible:ring-2',
              isActive
                ? 'bg-chariot-background-primary text-chariot-text-primary shadow-xs'
                : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
